import PropTypes from 'prop-types';
import React, { useContext, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { StandardButton } from '@eika/button';
import classNames from 'classnames';
import ahvContext from '../../../ahv-context';
import { emptyList } from '../../../constants';
import getLabel from '../labels';
import DisponentDetaljer, { getDisponentDetaljerFields } from './disponent-detaljer';
import { answersToManager, isValidDisponent } from './validation';
import CountryContext from '../../../country-context';

function DisponentListQuestionType({ question, appNamespace }) {
    const context = useContext(ahvContext);
    const countryContext = useContext(CountryContext);

    const { ekstratekster = {}, config } = question;

    const getShowErrors = useCallback(() => context.getHasSubmitted(), [context]);

    const getInvalidList = useCallback(
        (answer) => {
            const keys = Object.keys(getDisponentDetaljerFields());
            const manager = answersToManager(answer);
            return keys.filter((key) =>
                isValidDisponent(key, manager, config, countryContext.getNorwegianPostalCodesOptions) ? false : key
            );
        },
        [countryContext.getNorwegianPostalCodesOptions, config]
    );

    const addPrefilled = useCallback((disponentListString) => {
        let disponentList = JSON.parse(disponentListString);
        if (!Array.isArray(disponentList)) {
            disponentList = [disponentList];
        }
        return disponentList.map((disponent) => {
            if (disponent.aktorId) disponent.fnr = disponent.aktorId.value;
            return {
                disponentKey: uuid(),
                ...getDisponentDetaljerFields(),
                ...disponent,
            };
        });
    }, []);

    const addDisponent = useCallback(() => {
        const currentDisponentList = context.getValue(question.definisjon);
        const disponentList = [
            ...currentDisponentList,
            {
                disponentKey: uuid(),
                ...getDisponentDetaljerFields(),
            },
        ];
        context.onChange(question.definisjon, { value: disponentList });
    }, [context, question.definisjon]);

    const getUpdateDisponent = useCallback(
        (disponentKey) => (data) => {
            const currentDisponentList = context.getValue(question.definisjon);
            const disponentList = currentDisponentList.map((disponent) => {
                if (disponent.disponentKey !== disponentKey) {
                    return disponent;
                }
                const modifiedDisponent = { ...disponent, ...data };
                const hasError = getShowErrors() ? getInvalidList(modifiedDisponent).length > 0 : false;
                return { ...modifiedDisponent, error: hasError };
            });
            context.onChange(question.definisjon, { value: disponentList });
        },
        [context, question.definisjon, getInvalidList, getShowErrors]
    );

    const getRemoveDisponent = useCallback(
        (disponentKey) => () => {
            const currentDisponentList = context.getValue(question.definisjon);
            const disponentList = currentDisponentList.filter((disponent) => disponent.disponentKey !== disponentKey);
            context.onChange(question.definisjon, { value: disponentList });
        },
        [context, question.definisjon]
    );

    let disponentList = context.getValue(question.definisjon);
    if (typeof disponentList === 'string') {
        disponentList = addPrefilled(disponentList);
        context.onChange(question.definisjon, { value: disponentList });
    }

    return (
        <>
            {disponentList.map((disponent, i) => (
                <div key={`disponent-${disponent.disponentKey}`}>
                    <div style={{ fontSize: '20px' }} className="extra-margin-top">
                        {`${getLabel(ekstratekster, 'disp_card_title')} #${i + 1}`}
                        <button
                            style={{ float: 'right', margin: 'unset', padding: 'unset', height: 'unset' }}
                            className="ion-android-close removeicon remove-btn btn"
                            type="button"
                            onClick={getRemoveDisponent(disponent.disponentKey)}
                            aria-label="Remove disponent"
                        />
                    </div>
                    <DisponentDetaljer
                        context={context}
                        question={disponent}
                        onChange={getUpdateDisponent(disponent.disponentKey)}
                        errors={getShowErrors() ? getInvalidList(disponent) : emptyList}
                        tekster={ekstratekster}
                        index={i}
                        appNamespace={appNamespace}
                    />
                </div>
            ))}
            <StandardButton
                onClick={addDisponent}
                className={classNames({ 'extra-margin-top': disponentList && disponentList.length > 0 })}
            >
                {getLabel(ekstratekster, 'disp_aktor_add')}
            </StandardButton>
        </>
    );
}

DisponentListQuestionType.propTypes = {
    appNamespace: PropTypes.string,
    question: PropTypes.shape({
        definisjon: PropTypes.string.isRequired,
        utfyllingsregel: PropTypes.shape({
            regel: PropTypes.string.isRequired,
        }).isRequired,
        ekstratekster: PropTypes.shape({
            disp_card_title: PropTypes.string,
            disp_navn: PropTypes.string,
            disp_fnr: PropTypes.string,
            disp_fodeland: PropTypes.string,
            disp_statsborgerskap: PropTypes.string,
            disp_sekundart_statsborgerskap: PropTypes.string,
            disp_adresse: PropTypes.string,
            disp_postnummer: PropTypes.string,
            disp_poststed: PropTypes.string,
            disp_bostedsland: PropTypes.string,
            disp_pep: PropTypes.string,
            disp_peptype: PropTypes.string,
            disp_peprelasjon: PropTypes.string,
            aktor_add: PropTypes.string,
        }),
    }).isRequired,
};

export default DisponentListQuestionType;
